/**
 * Viewport Manager Service
 * Handles mobile Safari iframe viewport height issues
 * Uses window.innerHeight instead of vh/dvh units for mobile
 */

class ViewportManagerService {
  private initialized = false;
  private isIframe = false;
  private isMobile = false;
  private resizeHandler: (() => void) | null = null;
  private orientationQuery: MediaQueryList | null = null;
  private readonly orientationHandler = () => this.setupDynamicViewport();

  constructor() {
    this.detectEnvironment();
  }

  private detectEnvironment() {
    // Check if we're in an iframe
    this.isIframe = window.self !== window.top;
    
    // Check if we're on mobile (simple detection)
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth <= 768;
  }

  public initialize() {
    if (this.initialized) return;
    
    // Only apply dynamic height on mobile devices in iframes
    if (!this.isMobile) {
      this.initialized = true;
      return;
    }

    this.setupDynamicViewport();
    this.setupResizeListener();
    this.initialized = true;
  }

  private setupDynamicViewport() {
    const setViewportHeight = () => {
      const rootStyle = document.documentElement.style;
      let changed = false;
      // Every call re-runs at +100 / +500 ms; skipping unchanged values keeps those repeats free.
      const setIfChanged = (style: CSSStyleDeclaration, property: string, value: string) => {
        if (style.getPropertyValue(property) !== value) {
          style.setProperty(property, value);
          changed = true;
        }
      };

      // Populate CSS variables that mirror CSS env() safe-area insets
      // These are strings so CSS engine resolves env() at render time
      setIfChanged(rootStyle, '--safe-top', 'env(safe-area-inset-top)');
      setIfChanged(rootStyle, '--safe-right', 'env(safe-area-inset-right)');
      setIfChanged(rootStyle, '--safe-bottom', 'env(safe-area-inset-bottom)');
      setIfChanged(rootStyle, '--safe-left', 'env(safe-area-inset-left)');

      // Get the actual window inner height
      const height = window.innerHeight;

      // iOS Safari reports the *large* viewport in innerHeight — the size the page
      // would have with the address bar collapsed — while visualViewport is the box
      // actually on screen. Taking innerHeight leaves the app taller than the display,
      // pushing whatever sits at its bottom out of view.
      const visibleHeight = window.visualViewport?.height ?? height;
      setIfChanged(rootStyle, '--app-height', `${visibleHeight}px`);
      setIfChanged(rootStyle, '--viewport-offset', `${height - visibleHeight}px`);

      // The direct height writes below exist for the iframe embedding only.
      if (!this.isIframe) {
        return;
      }

      // Also directly set the app container height for immediate effect
      const appContainer = document.getElementById('lobby2-root');
      if (appContainer) {
        setIfChanged(appContainer.style, 'height', `${height}px`);
      }
      
      // Set parent container height if it exists (for iframe scenarios)
      const parentContainer = document.body;
      if (parentContainer) {
        setIfChanged(parentContainer.style, 'height', `${height}px`);
      }
      
      // Set html height as well
      setIfChanged(rootStyle, 'height', `${height}px`);

      if (!changed) {
        return;
      }

      // Force reflow for styled-components to pick up the new CSS custom property
      const styleElement = document.createElement('style');
      styleElement.textContent = '/* Force reflow for mobile navigation */';
      document.head.appendChild(styleElement);
      
      // Remove it immediately to trigger reflow without permanent changes
      setTimeout(() => {
        if (styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
      }, 1);
    };

    // Set initial height
    setViewportHeight();
    
    // Also set it after a short delay to handle any initialization delays
    setTimeout(setViewportHeight, 100);
    setTimeout(setViewportHeight, 500);
  }

  private setupResizeListener() {
    // Debounced resize handler to avoid too many updates
    let resizeTimeout: NodeJS.Timeout;
    
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.setupDynamicViewport();
      }, 150);
    };

    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);
    
    // Also listen for visual viewport changes (iOS Safari address bar)
    window.visualViewport?.addEventListener('resize', this.resizeHandler);

    // A rotation applies at once: through the debounce the old heights stayed for ~150 ms.
    this.orientationQuery = window.matchMedia('(orientation: portrait)');
    this.orientationQuery.addEventListener('change', this.orientationHandler);
  }

  public cleanup() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
      
      window.visualViewport?.removeEventListener('resize', this.resizeHandler);
      this.orientationQuery?.removeEventListener('change', this.orientationHandler);
      this.orientationQuery = null;

      this.resizeHandler = null;
    }
    
    this.initialized = false;
  }

  public forceRecalculate() {
    if (this.isMobile && this.isIframe) {
      this.setupDynamicViewport();
    }
  }

  // Get the calculated app height for components that need it
  public getAppHeight(): number {
    if (this.isMobile && this.isIframe) {
      return window.innerHeight;
    }
    return window.innerHeight; // Fallback
  }
}

// Export singleton instance
export const viewportManager = new ViewportManagerService();